import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";

import referralService from "./services/referralService";

import {
  buildReferralLink,
  buildReferralMessage,
  calculateWithdrawalProgress,
  validateSheba,
  formatToman,
} from "./utils/referralHelpers";

import ReferralHeader from "./component/referral/ReferralHeader";
import ReferralStats from "./component/referral/ReferralStats";
import ReferralActions from "./component/referral/ReferralActions";
import StoryGenerator from "./component/referral/StoryGenerator";
import WithdrawalModal from "./component/referral/WithdrawalModal";
import WithdrawalCard from "./component/referral/WithdrawalCard";

export default function ReferralScreen() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    iban: "",
    accountOwnerName: "",
  });

  const referralCode = data?.referralCode || data?.code || "";

  const shareLink = useMemo(() => {
    if (!referralCode) return window.location.origin;
    return buildReferralLink(referralCode);
  }, [referralCode]);

  const totalEarned = Number(data?.totalEarned || 0);
  const availableBalance = Number(data?.availableBalance || 0);
  const pendingBalance = Number(data?.pendingBalance || 0);
  const minimumWithdrawalAmount = Number(data?.minimumWithdrawalAmount || 0);

  const progress = calculateWithdrawalProgress(
    availableBalance,
    minimumWithdrawalAmount
  );

  const canWithdraw =
    minimumWithdrawalAmount > 0 &&
    availableBalance >= minimumWithdrawalAmount;

  async function loadReferral() {
    try {
      setErr("");
      setLoading(true);

      const result = await referralService.getMyReferral();
      setData(result);
    } catch {
      setErr("دریافت اطلاعات دعوت دوستان ناموفق بود");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReferral();
  }, []);

  async function copyText(text, type) {
    if (!text || text === "—") return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      setErr("کپی کردن انجام نشد");
    }
  }

  async function handleShare() {
    const message = buildReferralMessage(referralCode, shareLink);

    if (navigator.share) {
      try {
        await navigator.share({
          title: "دعوت به موجیم",
          text: message,
          url: shareLink,
        });
        return;
      } catch {}
    }

    await copyText(message, "message");
  }

  function validateWithdraw() {
    const amount = Number(withdrawForm.amount || 0);

    if (!amount || amount <= 0) {
      return "مبلغ برداشت را وارد کن";
    }

    if (minimumWithdrawalAmount > 0 && amount < minimumWithdrawalAmount) {
      return `حداقل مبلغ برداشت ${formatToman(minimumWithdrawalAmount)} است`;
    }

    if (amount > availableBalance) {
      return "مبلغ برداشت بیشتر از موجودی قابل برداشت است";
    }

    if (!validateSheba(withdrawForm.iban)) {
      return "شماره شبا معتبر نیست. مثال: IRxxxxxxxxxxxxxxxxxxxxxxxx";
    }

    if (!withdrawForm.accountOwnerName.trim()) {
      return "نام صاحب حساب را وارد کن";
    }

    return "";
  }

  async function submitWithdraw(e) {
    e.preventDefault();

    const validationError = validateWithdraw();

    if (validationError) {
      setErr(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setErr("");

      await referralService.submitWithdrawalRequest({
        amount: Number(withdrawForm.amount),
        iban: withdrawForm.iban.replace(/\s/g, "").toUpperCase(),
        accountOwnerName: withdrawForm.accountOwnerName.trim(),
      });

      setModalOpen(false);

      setWithdrawForm({
        amount: "",
        iban: "",
        accountOwnerName: "",
      });

      await loadReferral();
    } catch {
      setErr("ثبت درخواست برداشت ناموفق بود");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <main className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-purple-500 border-t-transparent animate-spin" />
            <p className="mt-4 text-slate-300">در حال دریافت اطلاعات...</p>
          </div>
        </main>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <main className="max-w-5xl mx-auto px-4 py-6 pb-20">
        <button
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-2 text-sm text-slate-300"
        >
          <FaArrowRight />
          برگشت
        </button>

        <ReferralHeader />

        {err && (
          <div className="mt-5 rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-200">
            {err}
          </div>
        )}

        <ReferralStats
          totalEarned={totalEarned}
          availableBalance={availableBalance}
          pendingBalance={pendingBalance}
        />

        {totalEarned === 0 && (
          <section className="mt-5 rounded-[2rem] border border-green-500/20 bg-green-500/10 p-5 text-center">
            <div className="text-2xl">🎁</div>

            <h2 className="mt-2 text-xl font-black text-white">
              اولین دوستت رو دعوت کن
            </h2>

            <p className="mt-2 text-sm text-green-100 leading-7">
              با هر خرید موفق از طرف دوستانت، درآمدت داخل کیف پولت ثبت می‌شود.
            </p>
          </section>
        )}

        <WithdrawalCard
          availableBalance={availableBalance}
          minimumWithdrawalAmount={minimumWithdrawalAmount}
          progress={progress}
          canWithdraw={canWithdraw}
          onOpen={() => setModalOpen(true)}
        />

        <ReferralActions
          referralCode={referralCode || "—"}
          copied={copied}
          copyText={copyText}
          shareLink={shareLink}
          handleShare={handleShare}
        />

        <StoryGenerator
          referralCode={referralCode}
          shareLink={shareLink}
          onCopyLink={() => copyText(shareLink, "link")}
        />
      </main>

      {modalOpen && (
        <WithdrawalModal
          form={withdrawForm}
          setForm={setWithdrawForm}
          onClose={() => setModalOpen(false)}
          onSubmit={submitWithdraw}
          submitting={submitting}
          availableBalance={availableBalance}
          minimumWithdrawalAmount={minimumWithdrawalAmount}
        />
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div
      dir="rtl"
      className="min-h-screen font-vazir bg-[#070B14] text-white relative overflow-hidden"
    >
      <div className="fixed top-20 right-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
      <div className="fixed bottom-20 left-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />

      <div className="relative z-10">{children}</div>
    </div>
  );
}